import { AxiosError } from 'axios';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useValidation } from '../../../app/form/ValidationForm';
import { ButtonPrimary } from '../../common/components/buttons';
import { PasswordInput } from '../../common/components/inputs';
import {
  AuthHeader,
  AuthTitle,
  AuthFooter,
  PasswordRequirements,
} from '../components';
import { AuthMainContainer } from '../components/cards';
import { EditPasswordSuccess } from '../components/forgotPassword/EditPasswordSuccess';
import { usePasswordRequirements, useResetPassword } from '../hooks';

type FormValue = {
  password: string;
  passwordRepeat: string;
};

type ErrorResponse = {
  field_error?: Array<string>;
  reason_code?: string;
  error?: string;
};

export const PasswordEdit = () => {
  const { t } = useTranslation();
  const { key } = useParams();
  const Validation = useValidation();
  const {
    handleSubmit,
    formState: { errors },
    setError,
    register,
    watch,
  } = useForm<FormValue>();
  const passwordValue = watch('password', '');

  const navigate = useNavigate();

  const email = new URLSearchParams(window.location.search).get('email');
  const init = new URLSearchParams(window.location.search).get('init');
  const titleKey = init
    ? 'front.auth.passwordEdit.header-init'
    : 'front.auth.passwordEdit.header';

  const { isPasswordValid } = usePasswordRequirements(passwordValue);
  const onError = (error: AxiosError<ErrorResponse>) => {
    const data = error.response?.data;

    switch (data?.reason_code) {
      case 'invalid_reset_password_key':
        navigate(`../forgot_password?expired_token=1&email=${email || ''}`);
        break;
      case 'passwords_do_not_match':
        if (data?.field_error) {
          setError('passwordRepeat', {
            type: 'custom',
            message: data.field_error[1],
          });
        }
        break;
      default:
        if (data?.field_error) {
          setError('password', {
            type: 'custom',
            message: data.field_error[1],
          });
        } else {
          setError('password', {
            type: 'custom',
            message: t('front.auth.passwordEdit.form.errors.default'),
          });
        }
        break;
    }
  };

  const { mutate, isLoading, isSuccess } = useResetPassword({ onError });
  const onSubmit = (data: FormValue) =>
    mutate({
      password: data.password,
      password_confirm: data.passwordRepeat,
      key: key as string,
    });

  // if url parameter 'init' is present, use title "front.auth.passwordEdit.header-init" instead of
  // "front.auth.passwordEdit.header"

  return (
    <div className="h-screen w-full flex flex-col justify-between">
      <div>
        <AuthHeader />
        {isSuccess ? (
          <EditPasswordSuccess />
        ) : (
          <AuthMainContainer>
            <AuthTitle>{t(titleKey)}</AuthTitle>
            <p className="text-base2 mt-[34px]">
              {t('front.auth.passwordEdit.form.title')}
            </p>
            <form onSubmit={handleSubmit(onSubmit)}>
              <PasswordInput
                {...register('password', Validation.password)}
                label={
                  t('front.auth.passwordEdit.form.password.label') as string
                }
                placeholder={
                  t(
                    'front.auth.passwordEdit.form.password.placeholder',
                  ) as string
                }
                className="mt-[33px]"
                errorText={errors?.password}
              />
              <PasswordRequirements password={passwordValue} />
              <PasswordInput
                {...register('passwordRepeat', {
                  ...Validation.password,
                  validate: (value) =>
                    value === passwordValue ||
                    (t(
                      'front.auth.passwordEdit.form.errors.passwords_do_not_match',
                    ) as unknown as string),
                })}
                label={
                  t(
                    'front.auth.passwordEdit.form.passwordRepeat.label',
                  ) as string
                }
                placeholder={
                  t(
                    'front.auth.passwordEdit.form.passwordRepeat.placeholder',
                  ) as string
                }
                className="mt-[33px]"
                errorText={errors?.passwordRepeat}
              />
              <ButtonPrimary
                isLoading={isLoading}
                disabled={!isPasswordValid || isLoading}
                className="mt-[51px]"
                type="submit"
              >
                {t('front.auth.passwordEdit.form.submit')}
              </ButtonPrimary>
            </form>
          </AuthMainContainer>
        )}
      </div>
      <AuthFooter />
    </div>
  );
};
